/**
 * 2D geometry helpers used by dynamic-block actions (translate / stretch /
 * mirror). The Tier-1 math here is pure — no React, no drawing-model — to keep
 * it reusable on the server (DXF import validation) too.
 *
 * The evaluator applies these to BlockChildEntity instances. We stay
 * duck-typed over an "entity-like" shape and expose a per-entity
 * dispatcher that knows the AutoCAD rules for each primitive
 * (Line start/end, Circle center, Arc center, LWPOLYLINE vertices,
 * Text insertion point, Ellipse center, nested Block insertion point,
 * Attdef insertion point).
 *
 * The three HEAVY child kinds `blockChildEntitySchema` also admits — a heavy
 * `Polyline`, an `ACAD_TABLE` and an `OLE2FRAME` — are NOT re-implemented here.
 * Every dispatcher below routes them to {@link ./geometryPolylineTableOle},
 * which delegates to `command-engine`'s one `transformEntityCoords` truth (and
 * to the refusal predicates that go with it). Before that routing existed they
 * fell out of each `default:` arm UNCHANGED, so a Move action left such a child
 * behind and an Array action stacked every copy on the original — GitHub Codex
 * review PR #785, thread 3997328676. That module's header carries the
 * dependency-graph decision and the per-kind reasoning.
 *
 * The point-level primitives and the duck-typed shapes live in the
 * ZERO-dependency leaf {@link ./geometryPoints} so this file and its
 * `geometryPolylineTableOle` sibling can both use them without importing each
 * other (a module cycle `.dependency-cruiser.cjs` fails as an ERROR). They are
 * re-exported below, so `from "./geometry"` and the package barrel still
 * resolve every one of them — no external import path changed.
 */

import type { Vec2, CrossingFrame, EntityLike } from "./geometryPoints";
// NOTE (dep-graph-refactor skill, pitfall 3): `export *` alone would publish
// these to OTHER modules without binding them in THIS one. The value import
// below is what makes the dispatchers able to CALL them.
import {
  pointInFrame,
  reflectPoint,
  rotatePoint,
  scalePoint,
  translatePoint,
} from "./geometryPoints";
import type { PolylineTableOleRefusalSink } from "./geometryPolylineTableOle";
import {
  isPolylineTableOleEntityLike,
  scalePolylineTableOleChild,
  stretchPolylineTableOleChild,
  transformPolylineTableOleChild,
} from "./geometryPolylineTableOle";

export type { Vec2, CrossingFrame, EntityLike } from "./geometryPoints";
export {
  pointInFrame,
  reflectPoint,
  rotatePoint,
  scalePoint,
  translatePoint,
} from "./geometryPoints";
export type {
  PolylineTableOleRefusal,
  PolylineTableOleRefusalReason,
  PolylineTableOleRefusalSink,
} from "./geometryPolylineTableOle";

/**
 * A justified Text / MText / Attdef whose visual anchor is the ALIGNMENT
 * point (DXF 11/21, group 72/74 nonzero) carries `alignmentPoint` as a SECOND
 * defining point that must ride EVERY dynamic action (translate / stretch /
 * rotate / scale / mirror) in lock-step with `insertionPoint` — otherwise a
 * Move/Array/Stretch on a justified ATTDEF moves only the insertion point and
 * leaves the alignment anchor (which downstream renderers actually place the
 * glyph at — `attdefFromEntity.ts resolveTemplateAnchor`, the PDF plot twin
 * `insertAttributes.ts resolveTemplateAnchor`) at the STALE authored spot, so
 * the label detaches from the dynamic geometry. Returns the transformed
 * `alignmentPoint` field ONLY when the entity actually carries one (left /
 * baseline text has no 11/21 point) so a non-justified entity's output stays
 * byte-identical. Mirrors the render/export convention that treats the
 * alignment point as a real geometry point, not just a style hint.
 */
function alignmentPointOf(entity: EntityLike): Vec2 | undefined {
  const ap = (entity as { alignmentPoint?: unknown }).alignmentPoint;
  return ap !== null &&
    typeof ap === "object" &&
    typeof (ap as Vec2).x === "number" &&
    typeof (ap as Vec2).y === "number"
    ? (ap as Vec2)
    : undefined;
}

function movedAlignmentPoint<T extends EntityLike>(
  entity: T,
  map: (p: Vec2) => Vec2,
): { alignmentPoint?: Vec2 } {
  const ap = alignmentPointOf(entity);
  return ap ? { alignmentPoint: map(ap) } : {};
}

/**
 * DXF horizontal (group 72) / vertical (group 74) justification: any nonzero
 * value makes the entity's VISUAL anchor its ALIGNMENT point (11/21) instead of
 * the insertion point (10/20). Twin of the render / export predicate
 * (`insertAttributes.ts usesAlignmentPoint`, `textConverter.ts
 * usesAlignmentPoint`, `attdefFromEntity.ts resolveTemplateAnchor`) — duplicated
 * here (not imported) so this server-safe math leaf keeps its zero drawing-model
 * dependency (CLAUDE.md ПРИОРИТЕТ #2). A default / left-baseline entity is NOT
 * anchored at 11/21 even if it happens to carry one.
 */
function usesAlignmentPointAnchor(entity: EntityLike): boolean {
  const h = (entity as { hJustification?: unknown }).hJustification;
  const v = (entity as { vJustification?: unknown }).vJustification;
  return (
    (typeof h === "number" && h !== 0) || (typeof v === "number" && v !== 0)
  );
}

/**
 * The alignment point that is an ACTIVE stretch grip: `alignmentPoint` ONLY when
 * the entity is JUSTIFIED (group 72/74 nonzero) so the render/export twins
 * actually anchor its glyph there. A default/left-baseline entity that merely
 * carries a stale `alignmentPoint` (the model permits it; the anchor resolvers
 * ignore it) returns `undefined` — a crossing frame around that unused point
 * must NOT drag a label that still plots at its `insertionPoint`.
 */
function activeAlignmentGrip(entity: EntityLike): Vec2 | undefined {
  return usesAlignmentPointAnchor(entity)
    ? alignmentPointOf(entity)
    : undefined;
}

/**
 * Transform a Text / MText / Attdef entity's ANCHOR points — its
 * `insertionPoint` and, for a justified entity, its `alignmentPoint` (see
 * `movedAlignmentPoint`) — by the SAME point map, carrying every other field
 * through. `extra` supplies the per-action scalar fields that are NOT points
 * (the `rotation` bump for rotate, the `height` scale for uniform scale). ONE
 * truth shared by translate / stretch / rotate / scale / mirror so the
 * anchor-pair transform (esp. the alignment-point carry) can never drift
 * between actions.
 */
function mapTextAnchors<T extends EntityLike>(
  entity: T,
  map: (p: Vec2) => Vec2,
  extra: Record<string, unknown> = {},
): T {
  const e = entity as unknown as { insertionPoint: Vec2 };
  return {
    ...entity,
    insertionPoint: map(e.insertionPoint),
    ...movedAlignmentPoint(entity, map),
    ...extra,
  };
}

/* ── Entity-level helpers ───────────────────────────────────────── */

/**
 * Translate every "defining point" of an entity by `delta`. The switch
 * mirrors `renderBlockChildEntity` supported shapes — Line/Circle/Arc/
 * LWPOLYLINE/Text/MText/Ellipse/nested Block/Attdef.
 *
 * Returns a shallow clone with translated fields. Fields not touched
 * by translation (color, layer, thickness, rotation, radius, etc.) are
 * preserved via `{ ...entity, <fields> }`.
 *
 * Unknown entity types are returned unchanged — defensive behavior so
 * future primitives don't silently break the evaluator; the caller can
 * log a warning at a higher level if desired.
 */
export function translateEntity<T extends EntityLike>(
  entity: T,
  delta: Vec2,
  refusals?: PolylineTableOleRefusalSink,
): T {
  if (delta.x === 0 && delta.y === 0) return entity;
  // Heavy `Polyline` / `Table` / `OLE Frame` — delegated, never re-implemented.
  // A pure translation's linear part is the IDENTITY, so no kind can decline it
  // (the table keeps its size and direction bit-exactly, the OLE frame's map is
  // diagonal); the sink is threaded anyway so the contract is uniform across
  // the five dispatchers.
  if (isPolylineTableOleEntityLike(entity)) {
    return transformPolylineTableOleChild(
      entity,
      (p) => translatePoint(p, delta),
      refusals,
    );
  }
  switch (entity.type) {
    case "Line": {
      const e = entity as unknown as {
        start: Vec2;
        end: Vec2;
      };
      return {
        ...entity,
        start: translatePoint(e.start, delta),
        end: translatePoint(e.end, delta),
      };
    }
    case "Circle":
    case "Arc": {
      const e = entity as unknown as { center: Vec2 };
      return { ...entity, center: translatePoint(e.center, delta) };
    }
    case "Ellipse": {
      const e = entity as unknown as { center: Vec2 };
      return { ...entity, center: translatePoint(e.center, delta) };
    }
    case "LWPOLYLINE": {
      const e = entity as unknown as {
        vertices: ReadonlyArray<
          { x: number; y: number } & Record<string, unknown>
        >;
      };
      return {
        ...entity,
        vertices: e.vertices.map((v) => ({
          ...v,
          x: v.x + delta.x,
          y: v.y + delta.y,
        })),
      };
    }
    case "Text":
    case "MText":
    case "Attdef":
      // The insertion point AND (for a justified entity) the alignment anchor
      // both translate — `mapTextAnchors` / `movedAlignmentPoint`.
      return mapTextAnchors(entity, (p) => translatePoint(p, delta));
    case "Block": {
      const e = entity as unknown as { insertionPoint: Vec2 };
      return {
        ...entity,
        insertionPoint: translatePoint(e.insertionPoint, delta),
      };
    }
    default:
      return entity;
  }
}

/**
 * AutoCAD-style STRETCH: only the vertices whose coordinate lies inside
 * the crossing frame are translated. Entities are classified per-type:
 *
 *   - Line: each endpoint tested independently; either/both may move.
 *   - Circle/Arc/Ellipse: center tested — inside → whole entity moves,
 *     outside → unchanged. AutoCAD refuses to stretch individual arc
 *     quadrants (matches real STRETCH command behavior).
 *   - LWPOLYLINE: per-vertex — each vertex inside frame moves.
 *   - Text/MText/Attdef: EITHER grip test — the insertion point OR (for a
 *     justified entity) the alignment anchor inside → the whole label moves,
 *     both anchors in lock-step; neither inside → keep.
 *   - Block: insertion point test; inside → move, outside → keep.
 *
 * Unknown types return unchanged (defensive).
 */
export function stretchEntity<T extends EntityLike>(
  entity: T,
  frame: CrossingFrame,
  delta: Vec2,
  refusals?: PolylineTableOleRefusalSink,
): T {
  if (delta.x === 0 && delta.y === 0) return entity;
  // Heavy `Polyline` (per-vertex, exactly like the LWPOLYLINE case below),
  // `Table` and `OLE Frame` (whole-unit move on a grip test, like `Block`) —
  // see `stretchPolylineTableOleChild` for why the latter two cannot stretch
  // and for the one refusal this action can raise.
  if (isPolylineTableOleEntityLike(entity)) {
    return stretchPolylineTableOleChild(entity, frame, delta, refusals);
  }
  switch (entity.type) {
    case "Line": {
      const e = entity as unknown as { start: Vec2; end: Vec2 };
      const startIn = pointInFrame(e.start, frame);
      const endIn = pointInFrame(e.end, frame);
      if (!startIn && !endIn) return entity;
      return {
        ...entity,
        start: startIn ? translatePoint(e.start, delta) : e.start,
        end: endIn ? translatePoint(e.end, delta) : e.end,
      };
    }
    case "Circle":
    case "Arc":
    case "Ellipse": {
      const e = entity as unknown as { center: Vec2 };
      return pointInFrame(e.center, frame)
        ? { ...entity, center: translatePoint(e.center, delta) }
        : entity;
    }
    case "LWPOLYLINE": {
      const e = entity as unknown as {
        vertices: ReadonlyArray<
          { x: number; y: number } & Record<string, unknown>
        >;
      };
      let moved = false;
      const nextVertices = e.vertices.map((v) => {
        if (pointInFrame({ x: v.x, y: v.y }, frame)) {
          moved = true;
          return { ...v, x: v.x + delta.x, y: v.y + delta.y };
        }
        return v;
      });
      return moved ? { ...entity, vertices: nextVertices } : entity;
    }
    case "Text":
    case "MText":
    case "Attdef": {
      const e = entity as unknown as { insertionPoint: Vec2 };
      // A text object moves as a UNIT if EITHER of its defining grips lies
      // inside the crossing frame — the insertion point (10/20) OR, for a
      // JUSTIFIED entity, its alignment anchor (11/21). AutoCAD STRETCH moves
      // the whole label when ANY of its grips is selected, and the VISIBLE grip
      // of a center/right/aligned-justified text is the alignment point (which
      // downstream renderers / the PDF plot twin actually place the glyph at),
      // NOT the insertion point. Testing insertionPoint alone left a justified
      // ATTDEF behind when only its alignment grip was inside the frame. But the
      // alignment point counts as a grip ONLY when the entity is actually
      // justified (`activeAlignmentGrip` gates on group 72/74) — a
      // default/left-baseline label carrying a STALE 11/21 point (which the
      // anchor resolvers ignore) must not be dragged by a frame around that
      // unused point alone (@codex #507). When a real grip is inside,
      // `mapTextAnchors` translates BOTH points in lock-step so the pair stays
      // coherent (`movedAlignmentPoint`).
      const alignmentPoint = activeAlignmentGrip(entity);
      const insideFrame =
        pointInFrame(e.insertionPoint, frame) ||
        (alignmentPoint !== undefined && pointInFrame(alignmentPoint, frame));
      return insideFrame
        ? mapTextAnchors(entity, (p) => translatePoint(p, delta))
        : entity;
    }
    case "Block": {
      const e = entity as unknown as { insertionPoint: Vec2 };
      return pointInFrame(e.insertionPoint, frame)
        ? { ...entity, insertionPoint: translatePoint(e.insertionPoint, delta) }
        : entity;
    }
    default:
      return entity;
  }
}

/**
 * Rotate every defining point of an entity around `center` by `angleRad`
 * (CCW, radians). Mirrors the entity-type switch of translateEntity —
 * Line, Circle, Arc, Ellipse, LWPOLYLINE, Text, MText, Attdef, Block.
 *
 * For rotationally-significant primitives:
 *   - Arc: rotate center AND shift startAngle / endAngle by angleDeg
 *   - Ellipse: rotate center AND majorAxisEndpoint vector
 *   - Text / MText: rotate insertion point AND (optionally) bump rotation
 *     field by angleDeg so the baseline stays visually consistent.
 *   - Block: rotate insertionPoint AND bump rotation field by angleDeg.
 */
export function rotateEntity<T extends EntityLike>(
  entity: T,
  center: Vec2,
  angleRad: number,
  refusals?: PolylineTableOleRefusalSink,
): T {
  if (angleRad === 0) return entity;
  // Heavy `Polyline` (bulges keep their handedness — a rotation has `det = +1`
  // — while group-50 curve-fit tangents turn), `Table` (its group-11/21/31
  // horizontal-direction vector IS its orientation) and `OLE Frame` (DECLINED
  // unless the angle is a multiple of 90°, because the entity has no rotation
  // field and its two stored corners cannot name an oblique rectangle).
  if (isPolylineTableOleEntityLike(entity)) {
    return transformPolylineTableOleChild(
      entity,
      (p) => rotatePoint(p, center, angleRad),
      refusals,
    );
  }
  const angleDeg = (angleRad * 180) / Math.PI;
  switch (entity.type) {
    case "Line": {
      const e = entity as unknown as { start: Vec2; end: Vec2 };
      return {
        ...entity,
        start: rotatePoint(e.start, center, angleRad),
        end: rotatePoint(e.end, center, angleRad),
      };
    }
    case "Circle": {
      const e = entity as unknown as { center: Vec2 };
      return { ...entity, center: rotatePoint(e.center, center, angleRad) };
    }
    case "Arc": {
      const e = entity as unknown as {
        center: Vec2;
        startAngle: number;
        endAngle: number;
      };
      return {
        ...entity,
        center: rotatePoint(e.center, center, angleRad),
        startAngle: e.startAngle + angleDeg,
        endAngle: e.endAngle + angleDeg,
      };
    }
    case "Ellipse": {
      const e = entity as unknown as {
        center: Vec2;
        majorAxisEndpoint: Vec2;
      };
      // majorAxisEndpoint is a vector relative to center — rotate it as a
      // direction (around origin 0,0), then rotate the center point.
      const newCenter = rotatePoint(e.center, center, angleRad);
      const newMajor = rotatePoint(
        e.majorAxisEndpoint,
        { x: 0, y: 0 },
        angleRad,
      );
      return {
        ...entity,
        center: newCenter,
        majorAxisEndpoint: newMajor,
      };
    }
    case "LWPOLYLINE": {
      const e = entity as unknown as {
        vertices: ReadonlyArray<
          { x: number; y: number } & Record<string, unknown>
        >;
      };
      return {
        ...entity,
        vertices: e.vertices.map((v) => {
          const rp = rotatePoint({ x: v.x, y: v.y }, center, angleRad);
          return { ...v, x: rp.x, y: rp.y };
        }),
      };
    }
    case "Text":
    case "MText":
    case "Attdef": {
      const e = entity as unknown as { rotation?: number };
      // Insertion + alignment anchors rotate about the SAME center; the
      // `rotation` bump keeps the glyph baseline consistent (`mapTextAnchors`).
      return mapTextAnchors(entity, (p) => rotatePoint(p, center, angleRad), {
        rotation: (e.rotation ?? 0) + angleDeg,
      });
    }
    case "Block": {
      const e = entity as unknown as {
        insertionPoint: Vec2;
        rotation?: number;
      };
      return {
        ...entity,
        insertionPoint: rotatePoint(e.insertionPoint, center, angleRad),
        rotation: (e.rotation ?? 0) + angleDeg,
      };
    }
    default:
      return entity;
  }
}

/**
 * Scale every defining point of an entity about `center` with independent
 * X / Y factors. For uniform scale (sx === sy), radii / widths scale by the
 * same factor. For non-uniform scale on Circle / Arc, AutoCAD's real
 * semantic is "convert to Ellipse"; we leave that conversion to the
 * EXPLODE path (DYN-2 consumers typically scale LWPOLYLINE / Line /
 * Ellipse) and here we just scale the center — radius stays unchanged on
 * non-uniform. Documented edge case.
 */
export function scaleEntity<T extends EntityLike>(
  entity: T,
  center: Vec2,
  sx: number,
  sy: number,
  refusals?: PolylineTableOleRefusalSink,
): T {
  if (sx === 1 && sy === 1) return entity;
  // Heavy `Polyline` (positions through the delegate, widths through
  // `scaleEntityRadii` at `explodedBandWidthScale`'s factor), `Table` (DECLINED
  // at a genuine resize — its `*T<N>` graphics cannot follow — and DECLINED
  // again at a single-axis MIRROR, `sx·sy < 0`, which clears the resize gate
  // because both magnitudes are 1 yet is still a reflection the table cannot
  // name) and `OLE Frame` (its size IS its two corners, which the delegate's
  // four-corner fold scales exactly).
  if (isPolylineTableOleEntityLike(entity)) {
    return scalePolylineTableOleChild(
      entity,
      (p) => scalePoint(p, center, sx, sy),
      sx,
      sy,
      refusals,
    );
  }
  const uniform = sx === sy;
  switch (entity.type) {
    case "Line": {
      const e = entity as unknown as { start: Vec2; end: Vec2 };
      return {
        ...entity,
        start: scalePoint(e.start, center, sx, sy),
        end: scalePoint(e.end, center, sx, sy),
      };
    }
    case "Circle": {
      const e = entity as unknown as { center: Vec2; radius: number };
      return {
        ...entity,
        center: scalePoint(e.center, center, sx, sy),
        radius: uniform ? e.radius * sx : e.radius,
      };
    }
    case "Arc": {
      const e = entity as unknown as { center: Vec2; radius: number };
      return {
        ...entity,
        center: scalePoint(e.center, center, sx, sy),
        radius: uniform ? e.radius * sx : e.radius,
      };
    }
    case "Ellipse": {
      const e = entity as unknown as {
        center: Vec2;
        majorAxisEndpoint: Vec2;
        ratio?: number;
      };
      // Scale center; the majorAxisEndpoint is a vector — scale without
      // the translation.
      return {
        ...entity,
        center: scalePoint(e.center, center, sx, sy),
        majorAxisEndpoint: {
          x: e.majorAxisEndpoint.x * sx,
          y: e.majorAxisEndpoint.y * sy,
        },
      };
    }
    case "LWPOLYLINE": {
      const e = entity as unknown as {
        vertices: ReadonlyArray<
          { x: number; y: number } & Record<string, unknown>
        >;
      };
      return {
        ...entity,
        vertices: e.vertices.map((v) => {
          const sp = scalePoint({ x: v.x, y: v.y }, center, sx, sy);
          return { ...v, x: sp.x, y: sp.y };
        }),
      };
    }
    case "Text":
    case "MText":
    case "Attdef": {
      const e = entity as unknown as { height?: number };
      // Insertion + alignment anchors scale about the SAME center; a uniform
      // scale also grows the text height (`mapTextAnchors`).
      return mapTextAnchors(entity, (p) => scalePoint(p, center, sx, sy), {
        height: uniform ? (e.height ?? 0) * sx : e.height,
      });
    }
    case "Block": {
      const e = entity as unknown as {
        insertionPoint: Vec2;
        scaleX?: number;
        scaleY?: number;
      };
      return {
        ...entity,
        insertionPoint: scalePoint(e.insertionPoint, center, sx, sy),
        scaleX: (e.scaleX ?? 1) * sx,
        scaleY: (e.scaleY ?? 1) * sy,
      };
    }
    default:
      return entity;
  }
}

/**
 * Mirror every defining point of an entity across the axis through
 * `axisStart`–`axisEnd`. Line.start ↔ line.end are both reflected; a
 * circle's center is reflected but its radius is preserved. Arcs also
 * require start/end-angle flipping to preserve sweep direction after
 * mirroring — AutoCAD's MIRROR command handles this by negating the
 * angles and swapping start/end.
 *
 * For Tier 1 we support the entities enumerated in the renderer:
 * Line, Circle, Arc, Ellipse, LWPOLYLINE, Text, MText, Attdef, Block.
 */
export function mirrorEntity<T extends EntityLike>(
  entity: T,
  axisStart: Vec2,
  axisEnd: Vec2,
  refusals?: PolylineTableOleRefusalSink,
): T {
  // Heavy `Polyline` (a reflection has `det < 0`, so every vertex BULGE is
  // negated — "left of the chord" becomes "right of the reflected chord" — and
  // the curve-fit tangents reflect), `Table` (DECLINED: a reflection is the ONE
  // placement its insertion point + horizontal-direction pair cannot name, and
  // writing it would store a 180° TURN instead — the refusal the in-place
  // MIRROR command already makes, announced here through the same sink) and
  // `OLE Frame` (DECLINED unless the axis is horizontal, vertical, or one of
  // the two diagonals, which are the reflections its corner pair can express).
  if (isPolylineTableOleEntityLike(entity)) {
    return transformPolylineTableOleChild(
      entity,
      (p) => reflectPoint(p, axisStart, axisEnd),
      refusals,
    );
  }
  switch (entity.type) {
    case "Line": {
      const e = entity as unknown as { start: Vec2; end: Vec2 };
      return {
        ...entity,
        start: reflectPoint(e.start, axisStart, axisEnd),
        end: reflectPoint(e.end, axisStart, axisEnd),
      };
    }
    case "Circle": {
      const e = entity as unknown as { center: Vec2 };
      return { ...entity, center: reflectPoint(e.center, axisStart, axisEnd) };
    }
    case "Arc": {
      const e = entity as unknown as {
        center: Vec2;
        startAngle: number;
        endAngle: number;
      };
      // Reflect the center; swap & negate the angles about the mirror axis
      // angle so the sweep direction stays visually correct. Computed axis
      // angle in radians; arc angles in DXF are degrees.
      const axisAngleRad = Math.atan2(
        axisEnd.y - axisStart.y,
        axisEnd.x - axisStart.x,
      );
      const axisAngleDeg = (axisAngleRad * 180) / Math.PI;
      const nextStart = 2 * axisAngleDeg - e.endAngle;
      const nextEnd = 2 * axisAngleDeg - e.startAngle;
      return {
        ...entity,
        center: reflectPoint(e.center, axisStart, axisEnd),
        startAngle: nextStart,
        endAngle: nextEnd,
      };
    }
    case "Ellipse": {
      const e = entity as unknown as {
        center: Vec2;
        majorAxisEndpoint: Vec2;
      };
      // Ellipse's majorAxisEndpoint is a vector relative to center. We
      // reflect the endpoint as an absolute point and subtract the
      // reflected center to keep the vector semantics.
      const absEnd = {
        x: e.center.x + e.majorAxisEndpoint.x,
        y: e.center.y + e.majorAxisEndpoint.y,
      };
      const newCenter = reflectPoint(e.center, axisStart, axisEnd);
      const newAbsEnd = reflectPoint(absEnd, axisStart, axisEnd);
      return {
        ...entity,
        center: newCenter,
        majorAxisEndpoint: {
          x: newAbsEnd.x - newCenter.x,
          y: newAbsEnd.y - newCenter.y,
        },
      };
    }
    case "LWPOLYLINE": {
      const e = entity as unknown as {
        vertices: ReadonlyArray<
          { x: number; y: number } & Record<string, unknown>
        >;
      };
      return {
        ...entity,
        vertices: e.vertices.map((v) => {
          const ref = reflectPoint({ x: v.x, y: v.y }, axisStart, axisEnd);
          return { ...v, x: ref.x, y: ref.y };
        }),
      };
    }
    case "Text":
    case "MText":
    case "Attdef":
      // Insertion + alignment anchors reflect across the SAME axis
      // (`mapTextAnchors` / `movedAlignmentPoint`).
      return mapTextAnchors(entity, (p) => reflectPoint(p, axisStart, axisEnd));
    case "Block": {
      const e = entity as unknown as { insertionPoint: Vec2 };
      return {
        ...entity,
        insertionPoint: reflectPoint(e.insertionPoint, axisStart, axisEnd),
      };
    }
    default:
      return entity;
  }
}
